import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ArrowUpRight } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_URL = "https://www.google.com/maps/search/Sanjib+Kia+NH-20+Panikoili+Jajpur";

export default function GoogleRating() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rating: 5.0, userRatingCount: 416, reviews: [] }));
  }, []);

  const rating = data?.rating ?? 5.0;
  const count = data?.userRatingCount ?? 416;
  const reviews = data?.reviews || [];
  const link = data?.googleMapsUri || FALLBACK_URL;

  return (
    <section data-testid="google-rating-section" className="relative bg-[#0A0F14] py-20 sm:py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <Reveal>
            <Overline>Google Reviews</Overline>
            <div className="mt-5 flex items-end gap-6">
              <span data-testid="google-rating-score" className="font-display font-extrabold text-6xl sm:text-8xl text-white leading-none tracking-tight">
                {rating.toFixed(1)}
              </span>
              <div className="pb-1">
                <div className="flex gap-1.5" data-testid="google-rating-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="fill-[#FFFFFF] text-[#FFFFFF]" />
                  ))}
                </div>
                <p className="mt-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  {count} verified reviews{data?.live ? " · live from Google" : ""}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-md text-slate-400 leading-relaxed">
              Sales, service, delivery — every Google reviewer rates Sanjib Kia five stars.
              See what Jajpur says about the experience.
            </p>
            <a
              data-testid="google-reviews-link"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 border border-white/20 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.18em] px-7 py-3.5 clip-notch transition-colors duration-300"
            >
              Read Reviews on Google <ArrowUpRight size={14} />
            </a>
          </Reveal>
        </div>

        {reviews.length > 0 && (
          <div data-testid="google-reviews-grid" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.slice(0, 3).map((r, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <a
                  data-testid={`google-review-card-${i}`}
                  href={r.googleMapsUri || link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full bg-[#0D1822] border border-white/10 hover:border-[#FFFFFF]/40 transition-colors duration-500 clip-card p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {[...Array(r.rating || 5)].map((_, s) => (
                        <Star key={s} size={12} className="fill-[#FFFFFF] text-[#FFFFFF]" />
                      ))}
                    </div>
                    <span className="font-mono text-[10px] text-slate-500">{r.relativeTime}</span>
                  </div>
                  <p className="mt-4 text-sm text-slate-300 leading-relaxed line-clamp-4">{r.text}</p>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">— {r.author}</p>
                </a>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
